import type { FC } from 'react';
import React, { useState, useCallback } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Popup from '@atlaskit/popup';
import MoreIcon from '@atlaskit/icon/glyph/more';
import SettingsIcon from '@atlaskit/icon/glyph/settings';
import { LinkItem } from '@atlaskit/menu';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { CONTEXT_PATH } from '@confluence/named-routes';
import { SpaceWatchButton } from '@confluence/action-buttons/entry-points/spaceWatch';
import { SpaceStarMenuItem } from '@confluence/action-buttons/entry-points/spaceStar';
import type { WithFlagsProps } from '@confluence/flags';
import { withFlags } from '@confluence/flags';

import type { SpaceProp, ModifyStarProp } from '../types';

import { MenuButton } from './PresentationalComponents';

const i18n = defineMessages({
	menu: {
		id: 'space-directory.space.menu',
		defaultMessage: 'More actions',
		description: "Tooltip message on '...' menu button",
	},
	spaceSettings: {
		id: 'space-directory.space.settings.link',
		defaultMessage: 'Space settings',
		description: 'Link to the Settings page for a Confluence Space',
	},
});

const MenuComponent: FC<SpaceProp & ModifyStarProp & WithFlagsProps> = ({
	space,
	modifyStarCache,
}) => {
	const intl = useIntl();
	const [isOpen, setIsOpen] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onClick = useCallback(() => {
		setIsOpen((o) => !o);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'more',
				source: 'spaceDirectoryScreen',
				attributes: {
					spaceId: space.id,
				},
			},
		}).fire();
	}, [space.id, createAnalyticsEvent]);

	return (
		<Popup
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			content={() => <PopupContent space={space} modifyStarCache={modifyStarCache} />}
			trigger={(triggerProps) => (
				<Tooltip content={<FormattedMessage {...i18n.menu} />} hideTooltipOnMouseDown>
					<MenuButton
						{...(triggerProps as any)}
						onClick={onClick}
						isOpen={isOpen}
						aria-label={intl.formatMessage(i18n.menu)}
					>
						<MoreIcon label="" size="medium" />
					</MenuButton>
				</Tooltip>
			)}
			placement="bottom-start"
		/>
	);
};

const PopupContent: FC<SpaceProp & ModifyStarProp> = ({ space, modifyStarCache }) => {
	const analytics = {
		source: 'spaceDirectoryScreen',
		attributes: {
			spaceId: space.id,
		},
	};

	return (
		<div>
			<LinkItem
				iconBefore={<SettingsIcon label="" />}
				href={`${CONTEXT_PATH}/spaces/viewspacesummary.action?key=${space.alias || space.key}`}
				isDisabled={!space.currentUser.isAdmin}
				UNSAFE_shouldDisableRouterLink
			>
				<FormattedMessage {...i18n.spaceSettings} />
			</LinkItem>
			<SpaceWatchButton
				spaceId={space.id!}
				isWatched={space.currentUser.isWatched}
				analytics={analytics}
				inMenu
			/>
			<SpaceStarMenuItem
				spaceId={space.id!}
				spaceKey={space.key!}
				isDisabled={false}
				isStarred={space.currentUser.isFavourited}
				modifyStarCache={modifyStarCache}
				analytics={analytics}
			/>
		</div>
	);
};

export const Menu = withFlags(MenuComponent);
